import {
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ApprovalOutlined, BlockOutlined, Close, PendingOutlined } from '@mui/icons-material'
import axios from 'axios'
import { ReactElement, useMemo, useState } from 'react'
import { EditableCell } from 'src/EditQuote/steps/draft/EditableCell'
import { sewpTrHeaderType } from 'src/types'
import { catalogTableItemListType, useCatalogData } from '../useCatalogData'
import { TR_SEWP_COLUMNS } from './sewpTypes'
import DragAndDropUpload from '../UploadMod'
import '../CatalogItemList.css'

type TrLineStatus = 'Awaiting' | 'Approved' | 'Rejected'
type TrLine = Extract<catalogTableItemListType[number], { kind: 'sewpTr' }>

const STATUS_DISPLAY: Record<TrLineStatus, { color: 'default' | 'success' | 'error'; icon: ReactElement }> = {
  Awaiting: { color: 'default', icon: <PendingOutlined /> },
  Approved: { color: 'success', icon: <ApprovalOutlined /> },
  Rejected: { color: 'error', icon: <BlockOutlined /> },
}

const columns = TR_SEWP_COLUMNS.filter(col => col.key !== 'check')

function StatusChip({ status }: { status: TrLineStatus }) {
  return <Chip size="small" label={status} color={STATUS_DISPLAY[status].color} icon={STATUS_DISPLAY[status].icon} />
}

type SewpTrOverlayProps = {
  contractNumber: string
  trHeader: sewpTrHeaderType | null
  onClose: () => void
  onSubmitted: () => void
  onError: (message: string) => void
}

export default function SewpTrOverlay({ contractNumber, trHeader, onClose, onSubmitted, onError }: SewpTrOverlayProps) {
  const filters = useMemo(() => ({ RequestId: trHeader?.RequestId ?? '' }), [trHeader])
  const lineCount = trHeader?.TotalClins || 1
  const { catalogItems, loading } = useCatalogData(trHeader ? `${contractNumber}/tr` : '', 0, lineCount, filters)
  const [statuses, setStatuses] = useState<Partial<Record<string, TrLineStatus>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [emailPromptOpen, setEmailPromptOpen] = useState(false)
  const [unmatched, setUnmatched] = useState(0)

  if (!trHeader) return null

  const lines = catalogItems.filter((item): item is TrLine => item.kind === 'sewpTr')
  const statusOf = (line: TrLine) => statuses[line.id] ?? (line.Status as TrLineStatus)
  const trReviewed = trHeader.Status === 'Approved'
  const canSubmit = !trReviewed && lines.length > 0 && lines.every(line => statusOf(line) !== 'Awaiting')

  const handleSubmit = () => {
    setSubmitting(true)
    axios
      .post(`/catalog/${contractNumber}/tr/${trHeader.RequestId}/review`, {
        lines: lines.map(line => ({ RequestLineId: line.id, Status: statusOf(line) })),
      })
      .then(onSubmitted)
      .catch(err => onError(err.response?.data?.message ?? 'Failed to submit technical request'))
      .finally(() => setSubmitting(false))
  }

  return (
    <div className="sewp-tr-overlay">
      <div className="sewp-tr-overlay-header">
        <div className="sewp-tr-overlay-chips">
          <Chip label={trHeader.TrNumber} />
          <Chip label={trHeader.ContractNumber.toUpperCase()} />
          <Chip label={`TOTALCLINS: ${trHeader.TotalClins}`} />
          {!trReviewed && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button variant="outlined" size="small" onClick={() => setEmailPromptOpen(true)}>
                Upload SEWP Email
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!canSubmit}
                loading={submitting}
                onClick={handleSubmit}
              >
                Save Status Changes
              </Button>
            </>
          )}
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close technical request">
          <Close />
        </IconButton>
      </div>
      <DragAndDropUpload
        allowText
        contractNumber={contractNumber}
        open={emailPromptOpen}
        onClose={() => setEmailPromptOpen(false)}
        title="Upload SEWP Approval Email"
        uploadUrl={`/catalog/${contractNumber}/tr/${trHeader.RequestId}/parse`}
        accept={{ 'application/vnd.ms-outlook': ['.msg'] }}
        maxFiles={1}
        failedMessage={null}
        onUploadComplete={uploads => {
          console.log('uploads', uploads)
          const result = uploads[0]?.response as
            | { statuses: Partial<Record<string, TrLineStatus>>; unmatched: number }
            | undefined
          if (!result) return
          setStatuses(prev => ({ ...prev, ...result.statuses }))
          setUnmatched(result.unmatched)
        }}
      />
      {!trReviewed && unmatched > 0 && (
        <Typography variant="caption" className="sewp-tr-overlay-status">
          {unmatched} line(s) weren't found in the email. Set them manually before submitting.
        </Typography>
      )}
      {loading === 'error' ? (
        <Typography className="sewp-tr-overlay-status">
          Couldn't load this technical request. Please try again.
        </Typography>
      ) : (
        <TableContainer className="catalog-table-container">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="150px">Status</TableCell>
                {columns.map(col => (
                  <TableCell key={col.key} width={col.width} align={col.align}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading === 'loading'
                ? Array.from({ length: lineCount }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: columns.length + 1 }).map((__, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : lines.map(line => (
                    <TableRow key={line.id} hover>
                      <TableCell>
                        {trReviewed ? (
                          <StatusChip status={line.Status as TrLineStatus} />
                        ) : (
                          <Select
                            size="small"
                            variant="standard"
                            disableUnderline
                            value={statusOf(line)}
                            renderValue={value => <StatusChip status={value} />}
                            onChange={e =>
                              setStatuses(prev => ({ ...prev, [line.id]: e.target.value as TrLineStatus }))
                            }
                          >
                            <MenuItem value="Awaiting" className="sewp-tr-overlay-hidden-option">
                              Awaiting
                            </MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                          </Select>
                        )}
                      </TableCell>
                      {columns.map(col => (
                        <TableCell key={col.key} align={col.align}>
                          <EditableCell
                            value={line[col.key as keyof TrLine]}
                            canEdit={false}
                            onChange={() => undefined}
                            options={col.options}
                            inputType={col.inputType}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}
